import { useEffect, useRef, useState } from 'react';
import BasicModal from './BasicModal';
import MemoriesSection from './MemoriesSection';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { saveUserSettings } from '../../services/UserSettingsService';
import { Mixpanel } from '../../services/Mixpanel';
import {
  ClassesSelection,
  GradeSelection,
  InterventionSpecialistTypeSelection,
  RoleSelection,
  SchoolSelection,
  SubjectSelection,
  doPreferToKnowSchool,
  getInferredSubjectsFromClasses,
  getInitialSelections,
  isInterventionSpecialist,
} from './ProfileFields';

// All-at-once profile editor: every relevant question on one scrollable form
// with a single Save, unlike the step-by-step onboarding wizard
// (OnboardingWizardBTS2026). Section visibility follows the selected roles,
// exactly as the wizard's steps do.

const TRACKING_PREFIX = 'Edit Profile';

const getVisibleSections = (roles) => ({
  interventionSpecialistType: isInterventionSpecialist(roles),
  grades: roles.includes('classroom_teacher'),
  classes: roles.includes('classroom_teacher'),
  subjects: roles.includes('instructional_coach'),
});

const SECTION_ORDER = [
  'roles',
  'interventionSpecialistType',
  'grades',
  'classes',
  'subjects',
  'school',
];

const Section = ({ title, subheading, hint, innerRef, children }) => {
  return (
    <div ref={innerRef} className='flex flex-col gap-y-3 scroll-mt-4'>
      <div>
        <h3 className='text-base font-semibold leading-tight text-gray-900'>{title}</h3>
        {subheading && <p className='text-sm text-gray-500'>{subheading}</p>}
      </div>
      {children}
      {hint && <p className='text-sm text-amber-700'>{hint}</p>}
    </div>
  );
};

export const EditProfileModal = ({ showModal, setShowModal, initialSection = null }) => {
  const { diffitUser } = useDiffitUserContext();
  const [selections, setSelections] = useState(getInitialSelections(diffitUser));
  const [isAddSchoolFormShown, setIsAddSchoolFormShown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialFocusRef = useRef(null);
  const hasTrackedShownRef = useRef(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (hasTrackedShownRef.current) {
      return;
    }
    Mixpanel.track('Edit Profile Modal Shown');
    hasTrackedShownRef.current = true;
  }, []);

  const visibleSections = getVisibleSections(selections.roles);

  const missing = {
    roles: selections.roles.length === 0,
    interventionSpecialistType:
      visibleSections.interventionSpecialistType &&
      selections.roles.filter((r) => r.startsWith('intervention_specialist_')).length === 0,
    grades: visibleSections.grades && selections.grade_levels.length === 0,
    classes: visibleSections.classes && selections.classes.length === 0,
    subjects: visibleSections.subjects && selections.subjects.length === 0,
    school:
      selections.school_records.length === 0 && !selections.does_not_work_for_school_or_district,
  };
  const isSaveDisabled = isSaving || Object.values(missing).some(Boolean);

  // Opened at a specific section (a chat "Saved 1 memory" chip deep-links to 'memories').
  // After paint, so the section has a ref by the time we scroll to it.
  useEffect(() => {
    if (!initialSection) return;
    sectionRefs.current[initialSection]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [initialSection]);

  const onClose = () => {
    Mixpanel.track('Edit Profile - Closed');
    setShowModal(false);
  };

  const onClickSave = async () => {
    // Save stays clickable while incomplete so it can point at what's missing.
    const firstMissing = SECTION_ORDER.find((key) => missing[key]);
    if (firstMissing) {
      sectionRefs.current[firstMissing]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Only send sections the user was shown, so a hidden section's stale value
    // can't overwrite the server. The wizard behaves the same way.
    const settings = {
      // dedupe, just to be safe
      roles: Array.from(new Set(selections.roles)),
      school_records: Array.from(new Set(selections.school_records)),
      does_not_work_for_school_or_district: selections.does_not_work_for_school_or_district,
      did_complete_onboarding: true,
    };
    if (visibleSections.grades) {
      settings.grade_levels = Array.from(new Set(selections.grade_levels));
    }
    if (visibleSections.classes) {
      settings.classes = Array.from(new Set(selections.classes));
    }
    if (visibleSections.subjects) {
      settings.subjects = Array.from(new Set(selections.subjects));
    } else if (visibleSections.classes) {
      // Classroom teachers pick classes; infer subjects from them, as the wizard does.
      settings.subjects = Array.from(new Set(getInferredSubjectsFromClasses(selections.classes)));
    }

    Mixpanel.track('Edit Profile - Save Click', {
      roles: settings.roles,
      grade_levels: settings.grade_levels,
      classes: settings.classes,
      subjects: settings.subjects,
      does_not_work_for_school_or_district: settings.does_not_work_for_school_or_district,
    });

    setIsSaving(true);
    try {
      await saveUserSettings(settings);
      setShowModal(false);
    } catch {
      // the axios interceptor already showed the error modal
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={onClose}
      ariaLabel='Update your profile'
      dialogPadding='p-0'
      // dvh-capped panel that scrolls its own content — see .max-h-visible-viewport
      // in index.css for why vh leaves it unscrollable on iOS.
      maxHeight='max-h-visible-viewport'
      stickyCloseButton
      overflowY='overflow-y-auto overscroll-contain'
      maxWidth='sm:max-w-[560px]'
      icon={null}
      initialFocusRef={initialFocusRef}
      iconBackground={'bg-none'}>
      <div className='flex flex-col'>
        {/* pr-10 keeps the heading clear of the close button */}
        <div className='px-6 pr-10 pt-6 pb-4 sm:px-8 sm:pr-10 sm:pt-8'>
          <h2 className='text-2xl font-semibold leading-tight'>Update your profile</h2>
          <p className='text-sm text-gray-500'>
            Diffit uses this to tailor the resources it makes for you.
          </p>
        </div>
        <div className='flex flex-col gap-y-8 px-6 py-2 sm:px-8'>
          <Section
            title='What do you do?'
            subheading='Select all that apply'
            hint={missing.roles ? 'Select at least one role.' : null}
            innerRef={(el) => (sectionRefs.current.roles = el)}>
            <RoleSelection
              selections={selections}
              setSelections={setSelections}
              initialFocusRef={initialFocusRef}
              trackingPrefix={TRACKING_PREFIX}
              scrollable={false}
            />
          </Section>

          {visibleSections.interventionSpecialistType && (
            <Section
              title='Which of these student groups do you support?'
              subheading='Select all that apply'
              hint={missing.interventionSpecialistType ? 'Select at least one group.' : null}
              innerRef={(el) => (sectionRefs.current.interventionSpecialistType = el)}>
              <InterventionSpecialistTypeSelection
                selections={selections}
                setSelections={setSelections}
                trackingPrefix={TRACKING_PREFIX}
                keepBroadRoleWhenEmpty={false}
              />
            </Section>
          )}

          {visibleSections.grades && (
            <Section
              title='What grades are you teaching this year?'
              subheading='Select all that apply'
              hint={missing.grades ? 'Select at least one grade.' : null}
              innerRef={(el) => (sectionRefs.current.grades = el)}>
              <GradeSelection
                selections={selections}
                setSelections={setSelections}
                trackingPrefix={TRACKING_PREFIX}
              />
            </Section>
          )}

          {visibleSections.classes && (
            <Section
              title='What classes are you teaching this year?'
              subheading='Select all that apply'
              hint={missing.classes ? 'Select at least one class.' : null}
              innerRef={(el) => (sectionRefs.current.classes = el)}>
              <ClassesSelection
                selections={selections}
                setSelections={setSelections}
                trackingPrefix={TRACKING_PREFIX}
              />
            </Section>
          )}

          {visibleSections.subjects && (
            <Section
              title='What subjects are you teaching this year?'
              subheading='Select all that apply'
              hint={missing.subjects ? 'Select at least one subject.' : null}
              innerRef={(el) => (sectionRefs.current.subjects = el)}>
              <SubjectSelection
                selections={selections}
                setSelections={setSelections}
                trackingPrefix={TRACKING_PREFIX}
                scrollable={false}
              />
            </Section>
          )}

          <Section
            title='Where do you work?'
            subheading={
              doPreferToKnowSchool(selections.roles)
                ? "Search for your school's name"
                : 'Search for your school or district name'
            }
            hint={
              missing.school && !isAddSchoolFormShown
                ? "Search for your school, or check the box if you don't work for one."
                : null
            }
            innerRef={(el) => (sectionRefs.current.school = el)}>
            <SchoolSelection
              selections={selections}
              setSelections={setSelections}
              isAddSchoolFormShown={isAddSchoolFormShown}
              setIsAddSchoolFormShown={setIsAddSchoolFormShown}
              trackingPrefix={TRACKING_PREFIX}
              mixpanelLocation='edit_profile_modal'
            />
          </Section>

          <Section
            title='Your Memories'
            subheading='Diffit remembers these notes about your class and uses them to tailor every resource. Changes here save immediately.'
            innerRef={(el) => (sectionRefs.current.memories = el)}>
            <MemoriesSection />
          </Section>
        </div>
        {!isAddSchoolFormShown && (
          <div className='sticky bottom-0 flex justify-end gap-x-3 border-t border-gray-100 bg-white px-6 py-4 sm:px-8'>
            <button
              onClick={onClose}
              className='rounded-md px-4 py-2 font-semibold text-gray-600 hover:bg-gray-100'>
              Cancel
            </button>
            <button
              disabled={isSaving}
              aria-disabled={isSaveDisabled}
              onClick={onClickSave}
              className={`rounded-md px-4 py-2 font-semibold text-white ${
                isSaveDisabled ? 'bg-gray-300' : 'bg-emerald-600 hover:bg-emerald-500'
              }`}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </BasicModal>
  );
};
